// Shared ET date-range helpers for the Communication feature — the Metrics tab's presets
// (AdminPage.tsx) and the Insights tab's reports (components/insights/*) both use these, so
// they live in one place rather than either duplicating them or importing across a
// page/component boundary that would otherwise become circular (InsightsPanel is rendered
// from AdminPage.tsx and would need to import back from it).

// How often the live tabs (Calls, Metrics) re-fetch so newly-ingested Zoom calls appear
// without a manual reload. A manual Refresh button is always available too. Also the interval
// for the Insights tab's Intraday Volume report — the only Insights section that auto-refreshes.
export const POLL_MS = 20_000;

// Ranges are US Eastern calendar dates (the admins' reporting timezone) and weeks run
// Sunday–Saturday. Custom exposes explicit start/end date fields.
export type MetricsPreset = 'current-day' | 'current-week' | 'prior-week' | 'current-month' | 'prior-month' | 'custom';
export const METRICS_PRESETS: { id: MetricsPreset; label: string }[] = [
  { id: 'current-day', label: 'Current Day' },
  { id: 'current-week', label: 'Current Week' },
  { id: 'prior-week', label: 'Prior Week' },
  { id: 'current-month', label: 'Current Month' },
  { id: 'prior-month', label: 'Prior Month' },
  { id: 'custom', label: 'Custom' },
];

// Today's calendar date in ET as YYYY-MM-DD (the en-CA locale formats ISO-style).
export const etToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
// Date-only arithmetic in UTC-midnight space so the viewer's own timezone can't shift a day.
export const isoToDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
export const dateToIso = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (iso: string, n: number) => dateToIso(new Date(isoToDate(iso).getTime() + n * 86_400_000));
export const fmtRangeDate = (iso: string) =>
  isoToDate(iso).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

// All Calls card's Date Range filter (docs/requirements/admin-calls-filters-and-transcript.md,
// FR-2.1/OQ-4) — its own independent state from the Metrics tab's metricsPreset (switching one
// tab's range must never silently change what the other tab shows), sharing every preset
// Metrics already defines plus an "All" option Metrics never needs (a chart always needs
// *some* bounded range; a raw call list defaults to no constraint at all).
export type CallsDateRangePreset = 'all' | MetricsPreset;
export const CALLS_DATE_RANGE_PRESETS: { id: CallsDateRangePreset; label: string }[] = [
  { id: 'all', label: 'All' },
  ...METRICS_PRESETS,
];

export function presetRange(preset: MetricsPreset): { from: string; to: string } | null {
  const today = etToday();
  const d = isoToDate(today);
  switch (preset) {
    case 'current-day':
      return { from: today, to: today };
    case 'current-week': {
      const sunday = addDays(today, -d.getUTCDay());
      return { from: sunday, to: addDays(sunday, 6) };
    }
    case 'prior-week': {
      const sunday = addDays(today, -d.getUTCDay() - 7);
      return { from: sunday, to: addDays(sunday, 6) };
    }
    case 'current-month':
      return {
        from: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))),
        to: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))),
      };
    case 'prior-month':
      return {
        from: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1))),
        to: dateToIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0))),
      };
    default:
      return null;
  }
}

// Resolves a CallsDateRangePreset the same way presetRange() resolves a MetricsPreset, plus
// 'all' → null (no date constraint) and 'custom' → null (the caller derives Custom's range
// from its own customFrom/customTo fields, exactly like the Metrics tab's metricsRange).
export function callsPresetRange(preset: CallsDateRangePreset): { from: string; to: string } | null {
  if (preset === 'all') return null;
  return presetRange(preset);
}
